/**
 * What a refused photo SAYS to Brandon (M5 / S05-04).
 *
 * This is the half of D-026 that was deliberately left unwritten until the
 * photo UI existed. I-041 recorded the gap in these words: three caps are
 * enforced and "three different refusal messages are possible and none is
 * written". These are them.
 *
 * THREE RULES THEY ALL FOLLOW:
 *
 *   **No internal codes in the sentence.** `photo-count` and `CL013` are for
 *   logs and tests. A man on a roof with one glove off gets a sentence.
 *
 *   **Every refusal says what to DO.** "This Capture is full" is a wall;
 *   "remove one before adding another" is a way past it. A refusal Brandon
 *   cannot act on is indistinguishable from a bug.
 *
 *   **Nothing overstates what happened.** A photo refused at the limit was
 *   never staged, never uploaded and never partly saved, and the wording does
 *   not imply otherwise.
 */

import { MAX_STAGED_SOURCE_BYTES, type LimitRefusalCode } from '../../data/photos/photo-delivery';
import { MAX_SOURCE_BYTES, type PhotoRefusal } from '../../data/photos/photo-pipeline';

const mib = (bytes: number): string => `${String(Math.round(bytes / 1_048_576))} MB`;

/**
 * D-026's three limits, in Brandon's words.
 *
 * The precedence (count → individual size → staged budget) lives in
 * `evaluatePhotoLimits`, not here: this function names whichever limit
 * actually refused, and never has to know which one would have refused next.
 */
export function describeLimitRefusal(code: LimitRefusalCode): string {
  switch (code) {
    case 'photo-too-large':
      return `That photo is bigger than ${mib(MAX_SOURCE_BYTES)}, so Crewlink cannot keep it. Take it again at a smaller size.`;
    case 'staged-budget':
      // The budget is about the DEVICE, not this Capture — the sentence says
      // so, because "this Capture is full" would send Brandon to remove photos
      // that are not the problem.
      return `This device is holding the ${mib(MAX_STAGED_SOURCE_BYTES)} of photos it can keep at once. Let the uploads in progress finish, then add this one.`;
  }
}

/**
 * Staging itself failing. Not a limit, and no longer ever a network problem.
 *
 * The `count-unavailable` wording is gone with the server read that produced
 * it (H-4 correction): staging no longer contacts the server, so "check your
 * connection" would now be a false explanation for the only failure left —
 * the device being unable to keep the file.
 */
export function describeStagingFailure(): string {
  return 'This photo could not be kept on this device, so it was not added.';
}

/**
 * A photo the S05-02 pipeline refused.
 *
 * Its `reason` is already a sentence written for Brandon and is used verbatim —
 * rewriting it here would give the product two vocabularies for one refusal.
 * The file name is prefixed because a refusal during a twelve-photo
 * multi-select has to say WHICH photo.
 */
export function describePipelineRefusal(refusal: PhotoRefusal, fileName: string): string {
  const name = fileName.trim();
  return name.length > 0 ? `${name} — ${refusal.reason}` : refusal.reason;
}

/** The four delivery states, as they appear under a thumbnail. */
export const DELIVERY_STATE_LABEL = {
  pending: 'Waiting to upload',
  uploading: 'Uploading…',
  failed: 'Upload failed',
  // NOT "Saved" and not "Backed up": what is true is that the server confirmed
  // the full-size copy arrived (D-028), and that is what the word claims.
  delivered: 'Uploaded',
} as const;
